// Which of the person's pictures and clips are v2's (ai_app_backend): its picture makers name each `m_…` (from the
// order's id, `m{order}_{n}_{try}`), its clip makers each clip by its order's id (`o_…`: a clip, a longer clip, a
// joined story), and v1's ids are letters and digits, so a picture or clip is kept, published, deleted or edited by
// the API that made it, and v2's are never extended, joined or animated on v1 (v2 takes v1's by id). Tiny on purpose:
// the Create page's calls (generate/api.ts) read it on every press.

/** Whether an id is one of v2's pictures (`m_…`) or clips (`o_…`). */
export const isV2Media = (id: unknown): id is string =>
  typeof id === 'string' && (id.startsWith('m_') || id.startsWith('o_'))

/**
 * Whether a video request is made from one of v2's pictures or clips: the clip an extend lengthens, or the picture a
 * new clip animates (a clip from words makes its own picture, and v1 ignores a picture id left beside one). v1 can't
 * find v2's media, so such a request is v2's whatever the switch says, and never sent to v1 (src/lib/api/video-v2.ts).
 */
export const videoFromV2Media = (body: Record<string, unknown>): boolean =>
  body.extend === true
    ? isV2Media(body.extend_video_id)
    : !body.txt2VidData && isV2Media(body.base_image)

/**
 * Whether a picture on the Create page is unsaved, as v1's page told: it came as pixels (v1's unsaved result) or is one
 * of v2's not kept at delivery. v1 answered a kept picture (a Private order's) as its gallery's address; every one of
 * v2's pictures comes as an address, kept or not, so one of v2's is told apart by `kept` (create-v2.ts `kept_refs`),
 * never by its address. An unsaved one of v2's goes after 24 hours, its address with it (`expires_at`).
 */
export const unsavedPicture = (item: {
  isUrl?: unknown
  docRef?: unknown
  kept?: unknown
}): boolean => !item.isUrl || (isV2Media(item.docRef) && item.kept !== true)

/**
 * How "Chat with this image" and "Use as AI Character" save an unsaved picture before taking it. One of v2's is kept
 * privately: the character is the person's own, and v2 makes a character, or an AI Character picture, from any kept
 * picture of theirs. Publishing it asked publishing's stricter checks (the age check, the feed rule, the words judged
 * for everyone), so a picture that could be kept was refused with "This can't be published", and one that passed was
 * put on the person's profile and in the feeds without their asking (Discord, 29 Oct). v1's pixels are still published
 * Public: v1 answers a private one's path, which generating rejects.
 */
export const characterSaving = (item: {
  docRef?: unknown
}): 'Public' | 'Private' => (isV2Media(item.docRef) ? 'Private' : 'Public')

/**
 * Whether a Create result's menu offers Publish, as v1's page did: always for a clip; for a picture, when it is
 * unsaved (`unsavedPicture`): v1 offered no Publish for a kept one.
 */
export const offersPublish = (item: {
  type?: unknown
  isUrl?: unknown
  docRef?: unknown
  kept?: unknown
}): boolean => item.type === 'video' || unsavedPicture(item)

/**
 * Whether a Create result counts in the feed's unsaved reminders (the Save-All badge's count and the leave-page
 * warning) until it is downloaded or published there, as v1's page counted: a result without both an address and an
 * id, and an unsaved picture (`unsavedPicture`). v1 counted a clip with its id as saved, and so does this; one of v2's
 * unsaved pictures counts, where its address alone made it look saved (24 Oct).
 */
export const countsAsUnsaved = (item: {
  type?: unknown
  isUrl?: unknown
  docRef?: unknown
  kept?: unknown
}): boolean =>
  !item.isUrl || !item.docRef || (item.type !== 'video' && unsavedPicture(item))
